import type { MutableRefObject, Ref, RefCallback } from 'react'
import { useCallback } from 'react'

/**
 * Fans a slide's root node out to two owners.
 *
 * A slide's root element is needed in two places at once: the component that
 * renders it gates its tab order, and the parent measures its visibility to
 * decide *whether* to gate. Both must see the same node — measuring one element
 * and gating another is how the two drift apart.
 *
 * @param localRef ref owned by the rendering component (the gate root)
 * @param externalRef ref handed down by the parent, when this element is the
 *   slide root; omitted when some outer container is
 * @returns the callback ref to attach to the element
 */
export function useSlideRef<T extends HTMLElement>(
  localRef: MutableRefObject<T | null>,
  externalRef?: Ref<T>,
): RefCallback<T> {
  return useCallback(
    (node: T | null) => {
      localRef.current = node

      if (typeof externalRef === 'function') {
        externalRef(node)
      } else if (externalRef) {
        ;(externalRef as MutableRefObject<T | null>).current = node
      }
    },
    [localRef, externalRef],
  )
}
